/**
 * Project Settings Sidebar Navigation
 * 2-column layout with navigation items
 *
 * Sections:
 * - Overview: name, description, language, status, min duration
 * - Channels: per-channel processing (call/chat), inherits from org
 * - Quality: Prompt Profile, Script Adherence
 * - Alert Channels: where TEAM alerts go, and from which severity
 * - Routing: inbound routing conditions (self-saving)
 * - Delivery: CRM delivery rules (self-saving), behind `enableDeliveryRules`
 * - Danger Zone: archive / delete
 */

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Settings,
  ClipboardList,
  Shuffle,
  AlertTriangle,
  Radio,
  Send,
  Megaphone,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Select } from '@/components/ui/select';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';

/**
 * Every section the settings screen can show.
 *
 * The list is the source of truth for BOTH the type and the route's
 * `validateSearch` allowlist (`PROJECT_SETTINGS_SECTIONS` below). Keeping them
 * separate is how a section becomes unreachable: the nav renders it, the click
 * navigates to `?section=<id>`, `validateSearch` does not recognise the value,
 * drops it, and the screen silently falls back to overview — a tab that looks
 * present and does nothing.
 */
export const PROJECT_SETTINGS_SECTIONS = [
  'overview',
  'team',
  'quality',
  'alert_channels',
  'routing',
  'channels',
  'delivery',
  'danger_zone',
] as const;

export type ProjectSettingsSection = (typeof PROJECT_SETTINGS_SECTIONS)[number];

interface NavItem {
  id: ProjectSettingsSection;
  labelKey: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { id: 'overview', labelKey: 'projects.overviewTab', icon: Settings },
  // Who oversees this project. The same `project_users` rows Settings → Team
  // writes per person, reached from the project — which is how an admin
  // actually thinks about it ("who watches this one").
  { id: 'team', labelKey: 'projects.teamTab', icon: Users },
  { id: 'channels', labelKey: 'projects.channelsTab', icon: Radio },
  { id: 'quality', labelKey: 'projects.qualityTab', icon: ClipboardList },
  // The former 'alerts' tab (an enable toggle + a low-score threshold) is gone:
  // nothing on the server ever read `settings.alerts`, so both controls were
  // saved and ignored. What actually decides whether an alert fires is a rule
  // in business_alert_rules — see /settings?section=notifications.
  // Megaphone, not Send — Send is already the Delivery tab's icon.
  { id: 'alert_channels', labelKey: 'projects.alertChannelsTab', icon: Megaphone },
  { id: 'routing', labelKey: 'projects.routingTab', icon: Shuffle },
  { id: 'delivery', labelKey: 'projects.deliveryTab', icon: Send },
  { id: 'danger_zone', labelKey: 'projects.dangerZoneTab', icon: AlertTriangle },
];

/**
 * Nav items minus the sections whose feature flag is off.
 * `delivery` edits conditions but cannot set the fields a rule writes, so every
 * rule it produces is a no-op — hidden until the action editor exists.
 */
function useVisibleNavItems(): NavItem[] {
  const { isEnabled } = useFeatureFlags();

  return useMemo(
    () =>
      NAV_ITEMS.filter(
        (item) => item.id !== 'delivery' || isEnabled('enableDeliveryRules')
      ),
    [isEnabled]
  );
}

interface ProjectSettingsSidebarProps {
  activeSection: ProjectSettingsSection;
  onSectionChange: (section: ProjectSettingsSection) => void;
  className?: string;
}

export function ProjectSettingsSidebar({
  activeSection,
  onSectionChange,
  className,
}: ProjectSettingsSidebarProps) {
  const { t } = useTranslation(['projects', 'common']);
  const navItems = useVisibleNavItems();

  return (
    <nav
      className={cn('flex flex-col gap-1', className)}
      aria-label={t('projects.settingsNavAriaLabel', 'Project settings navigation')}
    >
      <ul className="space-y-1" role="list">
        {navItems.map((item) => {
          const isActive = activeSection === item.id;
          const Icon = item.icon;
          const label = t(item.labelKey, item.id);

          return (
            <li key={item.id} role="none">
              <button
                type="button"
                role="menuitem"
                onClick={() => onSectionChange(item.id)}
                className={cn(
                  'group relative w-full flex items-center gap-3 px-3 py-2 rounded-md min-h-[40px]',
                  // buttons default to text-align: center — two-line labels
                  // must stay left-aligned
                  'text-sm text-left transition-all duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-bg-highlight text-primary border-l-[3px] border-l-primary pl-[9px]'
                    : 'text-fg-secondary hover:bg-bg-elevated hover:text-fg'
                )}
                aria-current={isActive ? 'page' : undefined}
                aria-label={isActive ? `${label} (current)` : label}
                data-testid={`project-settings-nav-${item.id}`}
              >
                <Icon
                  className={cn(
                    'h-[18px] w-[18px] shrink-0 opacity-70',
                    isActive && 'opacity-100 text-primary'
                  )}
                  aria-hidden="true"
                />
                <span>{label}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * Mobile Project Settings Navigation (Dropdown)
 * Used on mobile breakpoints instead of sidebar
 */
interface ProjectSettingsMobileNavProps {
  activeSection: ProjectSettingsSection;
  onSectionChange: (section: ProjectSettingsSection) => void;
  className?: string;
}

export function ProjectSettingsMobileNav({
  activeSection,
  onSectionChange,
  className,
}: ProjectSettingsMobileNavProps) {
  const { t } = useTranslation(['projects', 'common']);
  const navItems = useVisibleNavItems();

  return (
    <div className={cn('relative', className)}>
      <Select
        value={activeSection}
        onValueChange={(val) => onSectionChange(val as ProjectSettingsSection)}
        options={navItems.map((item) => ({
          value: item.id,
          label: t(item.labelKey, item.id),
        }))}
        data-testid="project-settings-mobile-nav"
        size="md"
      />
    </div>
  );
}

/**
 * Get section title key and fallback for header display
 */
export function getSectionTitleKey(section: ProjectSettingsSection): {
  key: string;
  fallback: string;
} {
  const titleMap: Record<ProjectSettingsSection, { key: string; fallback: string }> = {
    overview: { key: 'projects.overviewTab', fallback: 'projects.overviewTab' },
    team: { key: 'projects.teamTab', fallback: 'projects.teamTab' },
    quality: { key: 'projects.qualityTab', fallback: 'projects.qualityTab' },
    alert_channels: {
      key: 'projects.alertChannelsTab',
      fallback: 'projects.alertChannelsTab',
    },
    routing: { key: 'projects.routingTab', fallback: 'projects.routingTab' },
    channels: { key: 'projects.channelsTab', fallback: 'projects.channelsTab' },
    delivery: { key: 'projects.deliveryTab', fallback: 'projects.deliveryTab' },
    danger_zone: { key: 'projects.dangerZoneTab', fallback: 'projects.dangerZoneTab' },
  };

  return titleMap[section] || { key: section, fallback: section };
}
